import React, { useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { isLogin } from './utils';

import {
  ACCESS_DENIED_ENDPOINT,
  AuthContext,
  LOGGED_IN_USER_INFO,
  LOGIN_ENDPOINT,
  PATH_KEY,
  ROLES,
  TARGET_ORG_ID_KEY,
} from './constants';

import { removeFromLocalStorage, setValueToLocalStorage } from './services/localStorage';

export default function PrivateRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  setValueToLocalStorage(PATH_KEY, location.pathname + location.search);
  const { userInfo } = useContext(AuthContext);

  const role = userInfo?.roles && userInfo?.roles[0];

  const requestedOrgID = new URLSearchParams(location.search).get('orgID');
  const isOrgMatched = isLogin() && (!requestedOrgID || userInfo?.['tenant-id'] === requestedOrgID);

  useEffect(() => {
    // orgID is only needed to steer the initial landing into the right org. Once matched,
    // strip it from the URL so a later org switch (which updates userInfo, not the URL)
    // isn't re-compared against this now-stale param and doesn't force a spurious logout.
    if (!requestedOrgID || !isOrgMatched) return;

    const params = new URLSearchParams(location.search);
    params.delete('orgID');
    const cleanedSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: cleanedSearch ? `?${cleanedSearch}` : '' },
      { replace: true }
    );
  }, [requestedOrgID, isOrgMatched]);

  if (role === ROLES.CANDIDATE) return <Navigate to={ACCESS_DENIED_ENDPOINT} />;

  if (requestedOrgID) {
    if (isOrgMatched) {
      // Already in the org the link expects - nothing pending.
      removeFromLocalStorage(TARGET_ORG_ID_KEY);
    } else {
      // Remember the org this link points to so the login/refresh flow can log the user into it.
      setValueToLocalStorage(TARGET_ORG_ID_KEY, requestedOrgID);

      if (isLogin()) {
        // Logged in to a different org than the link expects - force a fresh login.
        removeFromLocalStorage(LOGGED_IN_USER_INFO);
        return <Navigate to={LOGIN_ENDPOINT} />;
      }
    }
  }

  return (
    // TODO: It's better to safeguard our private routes but it should have to be somewhere else not here
    isLogin() ? <Outlet /> : <Navigate to={LOGIN_ENDPOINT} />
  );
}
